/**
 * 订单详情抽屉 - 活动信息区块
 *
 * 展示对接人/对接电话/活动名称/活动地址，支持内联切换为编辑表单
 * （非弹窗）。受控组件，编辑状态与保存回调均由父组件编排。
 */
import InfoRow from '../InfoRow';
import { mapSearchUrl } from '../utils';

interface Props {
  localContactPerson: string | null;
  localContactPhone: string | null;
  localActivityName: string | null;
  localActivityAddress: string | null;
  hasContactPerson: boolean;
  hasContactPhone: boolean;
  hasActivityName: boolean;
  hasActivityAddress: boolean;
  hasAnyActivityInfo: boolean;
  showActivityEdit: boolean;
  /** 后端投影 canEditOrder：非建单方与终态单只读，不给内联编辑入口 */
  canEdit: boolean;
  editContactPerson: string;
  editContactPhone: string;
  editActivityName: string;
  editActivityAddress: string;
  activityError: string;
  /** 保存时才发现对接电话格式不对：单独占一行红字，别混进整块错误里让人猜是哪格 */
  contactPhoneErr: string;
  activitySaving: boolean;
  /**
   * 导航入口：地址行那颗 chip 的点击行为（三档逻辑见 OrderDetailDrawer.handleActivityNav）。
   * anchor 的 href 仍保留关键词检索兜底——右键「在新标签页打开」与 JS 失效时都能走。
   */
  onNavigate: (address: string) => void;
  onOpenEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onEditContactPersonChange: (value: string) => void;
  onEditContactPhoneChange: (value: string) => void;
  onEditActivityNameChange: (value: string) => void;
  onEditActivityAddressChange: (value: string) => void;
}

export default function ActivityInfoSection({
  localContactPerson,
  localContactPhone,
  localActivityName,
  localActivityAddress,
  hasContactPerson,
  hasContactPhone,
  hasActivityName,
  hasActivityAddress,
  hasAnyActivityInfo,
  showActivityEdit,
  canEdit,
  editContactPerson,
  editContactPhone,
  editActivityName,
  editActivityAddress,
  activityError,
  contactPhoneErr,
  activitySaving,
  onNavigate,
  onOpenEdit,
  onCancelEdit,
  onSave,
  onEditContactPersonChange,
  onEditContactPhoneChange,
  onEditActivityNameChange,
  onEditActivityAddressChange,
}: Props) {
  return (
    <>
      {/* Activity info */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">活动信息</p>
          {!showActivityEdit && canEdit && (
            <button onClick={onOpenEdit} className="flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-2-line text-sm" /></div>
              编辑
            </button>
          )}
        </div>
        {showActivityEdit ? (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-medium text-teal-700 flex items-center gap-1.5"><i className="ri-edit-2-line text-sm" />修改活动信息</p>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">对接人</label>
              <input type="text" value={editContactPerson} onChange={e => onEditContactPersonChange(e.target.value)} placeholder="活动现场对接人姓名" className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">对接电话</label>
              <input type="tel" value={editContactPhone} onChange={e => onEditContactPhoneChange(e.target.value)} placeholder="对接人联系电话" className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all ${contactPhoneErr ? 'border-rose-400' : 'border-gray-200 focus:border-teal-400'}`} />
              {contactPhoneErr && <p className="text-xs text-rose-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line text-sm" />{contactPhoneErr}</p>}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">活动名称</label>
              <input type="text" value={editActivityName} onChange={e => onEditActivityNameChange(e.target.value)} placeholder="活动主题或名称" className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">活动地址</label>
              <input type="text" value={editActivityAddress} onChange={e => onEditActivityAddressChange(e.target.value)} placeholder="活动举办地点" className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all" />
            </div>
            {activityError && <p className="text-xs text-rose-500 flex items-center gap-1"><i className="ri-error-warning-line text-sm" />{activityError}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={onCancelEdit} disabled={activitySaving} className="flex-1 py-2 text-sm border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors bg-white">取消</button>
              <button onClick={onSave} disabled={activitySaving} className="flex-1 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
                {activitySaving ? <><i className="ri-loader-4-line animate-spin text-sm" />保存中...</> : <><i className="ri-check-line text-sm" />确认保存</>}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-4">
            {!hasAnyActivityInfo ? (
              <p className="text-sm text-gray-400">暂未填写活动信息</p>
            ) : (
              <>
                {hasContactPerson && <InfoRow icon="ri-user-2-line" label="对接人" value={localContactPerson!} />}
                {hasContactPhone && <InfoRow icon="ri-smartphone-line" label="对接电话" value={localContactPhone!} href={`tel:${localContactPhone}`} />}
                {hasActivityName && <InfoRow icon="ri-flag-line" label="活动名称" value={localActivityName!} />}
                {hasActivityAddress && (
                  <InfoRow
                    icon="ri-map-pin-line"
                    label="活动地址"
                    value={localActivityAddress!}
                    trailing={
                      <a
                        href={mapSearchUrl(localActivityAddress!)}
                        onClick={e => { e.preventDefault(); onNavigate(localActivityAddress!); }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        <i className="ri-route-line text-sm" />导航
                      </a>
                    }
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}